import React from 'react'
import { Box, useTheme } from '@mui/material'
import { getCategoryConfig } from '../../hooks/useBacktestRankings'

interface SparklineBarProps {
  value: number
  maxValue: number
  minValue?: number
  width?: number
  height?: number
  color?: string
  showValue?: boolean
  formatValue?: (value: number) => string
}

export default function SparklineBar({
  value,
  maxValue,
  minValue = 0,
  width = 60,
  height = 16,
  color,
  showValue = true,
  formatValue,
}: SparklineBarProps) {
  const theme = useTheme()

  // Calculate the percentage for the bar width
  const range = maxValue - minValue

  // Simple linear scaling from min to max
  let normalizedValue
  if (range === 0) {
    normalizedValue = 0.5 // If all values are the same, show 50% width
  } else {
    // For negative values, use magnitude (absolute value) for sizing
    // This ensures -10% has a bigger bar than -2%
    if (minValue < 0) {
      const magnitudeMax = Math.max(Math.abs(maxValue), Math.abs(minValue))
      const magnitudeValue = Math.abs(value)
      normalizedValue = magnitudeValue / magnitudeMax
    } else {
      normalizedValue = (value - minValue) / range
    }
  }

  const barWidth = Math.max(2, normalizedValue * (width - 4)) // Min 2px width

  // Default color based on value
  const defaultColor =
    value >= 0
      ? 'rgb(46, 240, 50)' // Green for positive
      : '#ff6b6b' // Red for negative

  const barColor = color || defaultColor

  // Format the value for display
  const displayValue = formatValue ? formatValue(value) : value.toString()

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: width,
        height: height,
      }}
    >
      {/* Visual Bar */}
      <Box
        sx={{
          width: barWidth,
          height: height - 4,
          backgroundColor: barColor,
          borderRadius: 1,
          minWidth: 2,
          opacity: 0.8,
        }}
      />

      {/* Value Text */}
      {showValue && (
        <Box
          sx={{
            fontSize: '0.75rem',
            fontWeight: 'medium',
            color: theme.palette.text.primary,
            minWidth: 0,
            flex: 1,
          }}
        >
          {displayValue}
        </Box>
      )}
    </Box>
  )
}

interface RankBadgeProps {
  rank: number
  size?: 'small' | 'medium'
}

export function RankBadge({ rank, size = 'small' }: RankBadgeProps) {
  const theme = useTheme()

  const getRankColor = () => {
    switch (rank) {
      case 1:
        return '#FFD700' // Gold
      case 2:
        return '#C0C0C0' // Silver
      case 3:
        return '#CD7F32' // Bronze
      default:
        return theme.palette.primary.main
    }
  }

  const sizeProps =
    size === 'small'
      ? { width: 20, height: 20, fontSize: '0.7rem' }
      : { width: 24, height: 24, fontSize: '0.8rem' }

  return (
    <Box
      sx={{
        ...sizeProps,
        backgroundColor: getRankColor(),
        color: 'white',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        flexShrink: 0,
      }}
    >
      {rank}
    </Box>
  )
}

interface CategoryBadgeProps {
  category: 'expectancy' | 'robust' | 'riskAdjusted' | 'steady'
  badge: string
  size?: 'small' | 'medium'
}

export function CategoryBadge({ category, badge }: CategoryBadgeProps) {
  const theme = useTheme()
  const categoryConfig = getCategoryConfig(category, theme)

  return (
    <Box sx={{ color: categoryConfig.color }} title={badge}>
      {categoryConfig.icon}
    </Box>
  )
}
